import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import { useAppContext } from "@/shared/hooks/useAppContext";
import { enqueueSnackbar } from "notistack";
import { TOAST } from "@/shared/constants/toastMessages";
import { trackEvent, EventType } from "@/shared/utils/core";
import { logActivity } from "@/shared/utils/activityLogger";
import { LAWYER_ROUTES } from "@/shared/constants/paths";

export interface PersonInvitation {
  id: number;
  estate_id: number;
  email: string;
  role: "admin" | "member" | "viewer";
  invitation_token: string;
  expires_at: string;
  status: "pending" | "accepted" | "declined" | "expired";
  created_at: string;
  updated_at?: string;
  invited_by_user_id: number;
  person: {
    full_name: string | null;
    first_name: string | null;
    last_name: string | null;
  } | null;
  invited_by: {
    full_name: string | null;
    email: string | null;
  } | null;
}

export interface SendInvitationParams {
  estateId: number;
  email: string;
  role: "admin" | "member" | "viewer";
  silent?: boolean;
  analytics?: {
    inviteSource: "triage" | "people_page" | "other";
    inviteeRole?: string;
    hasPhone?: boolean;
    hasPersonalNote?: boolean;
  };
  isAttorney?: boolean;
}

export interface AcceptInvitationParams {
  invitationToken: string;
}

type InvitationErrorCode = "already_pending" | "already_invited" | "already_member" | "self_invite";

const INVITATION_NOTICES: Record<InvitationErrorCode, string> = {
  already_pending: "They already have a pending invitation. Use Resend to send it again.",
  already_invited: "They have already been invited.",
  already_member: "They already have access to this estate.",
  self_invite: "You already have access to this estate.",
};

class InvitationError extends Error {
  readonly code: InvitationErrorCode;

  constructor(code: InvitationErrorCode) {
    super(INVITATION_NOTICES[code]);
    this.name = "InvitationError";
    this.code = code;
  }
}

function notifyInvitationFailure(error: Error, fallback: string) {
  if (error instanceof InvitationError) {
    enqueueSnackbar(error.message, { variant: "info" });
    return;
  }
  enqueueSnackbar(fallback, { variant: "error" });
}

export function usePersonInvitations(estateId?: number) {
  const { user } = useAppContext();
  const queryClient = useQueryClient();
  // Fetch pending invitations
  const {
    data: invitations = [],
    isLoading: isLoadingInvitations,
    error: invitationsError,
    refetch: refetchInvitations,
  } = useQuery({
    queryKey: ["personInvitations", estateId],
    queryFn: async (): Promise<PersonInvitation[]> => {
      if (!estateId) return [];

      const { data, error } = await supabase
        .from("estate_invitations")
        .select(
          `
          *,
          person:estates (
            full_name,
            first_name,
            last_name
          ),
          invited_by:users!estate_invitations_invited_by_user_id_fkey (
            full_name,
            email
          )
        `,
        )
        .eq("estate_id", estateId)
        .eq("status", "pending")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching invitations:", error);
        return [];
      }

      return data || [];
    },
    enabled: !!estateId && !!user?.user_id,
    staleTime: 1 * 60 * 1000,
    refetchInterval: 30 * 1000, // poll every 30s to detect accepted invites
  });

  // Fetch accepted invitations to check if users have joined
  const { data: acceptedInvitations = [] } = useQuery({
    queryKey: ["personInvitationsAccepted", estateId],
    queryFn: async (): Promise<PersonInvitation[]> => {
      if (!estateId) return [];

      const { data, error } = await supabase
        .from("estate_invitations")
        .select(
          `
          *,
          person:estates (
            full_name,
            first_name,
            last_name
          ),
          invited_by:users!estate_invitations_invited_by_user_id_fkey (
            full_name,
            email
          )
        `,
        )
        .eq("estate_id", estateId)
        .eq("status", "accepted")
        .order("created_at", { ascending: false });

      if (error) {
        console.error("Error fetching accepted invitations:", error);
        return [];
      }

      return data || [];
    },
    enabled: !!estateId && !!user?.user_id,
    staleTime: 1 * 60 * 1000,
    refetchInterval: 30 * 1000,
  });

  // Send invitation mutation - creates proper invitation with token
  const sendInvitation = useMutation({
    mutationFn: async ({ estateId, email, role, isAttorney }: SendInvitationParams) => {
      if (!user?.user_id) {
        throw new Error("User not authenticated");
      }

      // Check if there's already a pending invitation for this email and person
      const { data: existingInvitation } = await supabase
        .from("estate_invitations")
        .select("id")
        .eq("email", email.toLowerCase())
        .eq("estate_id", estateId)
        .eq("status", "pending")
        .single();

      if (existingInvitation) {
        throw new InvitationError("already_pending");
      }

      // Check if contact has already been invited and get contact info
      const { data: existingContact } = await supabase
        .from("estate_contacts")
        .select("id, invited, first_name, last_name")
        .eq("email", email.toLowerCase())
        .eq("estate_id", estateId)
        .single();

      if (existingContact?.invited) {
        throw new InvitationError("already_invited");
      }

      if (user.email && email.toLowerCase() === user.email.toLowerCase()) {
        throw new InvitationError("self_invite");
      }

      const { data: existingMember } = await supabase
        .from("users")
        .select("user_id")
        .eq("email", email.toLowerCase())
        .maybeSingle();

      if (existingMember) {
        const [{ data: alreadyOnEstate }, { data: alreadyLawyer }] = await Promise.all([
          supabase
            .from("user_estates")
            .select("user_id")
            .eq("user_id", existingMember.user_id)
            .eq("estate_id", estateId)
            .maybeSingle(),
          supabase
            .from("lawyer_estates")
            .select("lawyer_user_id")
            .eq("lawyer_user_id", existingMember.user_id)
            .eq("estate_id", estateId)
            .maybeSingle(),
        ]);

        if (alreadyOnEstate || alreadyLawyer) {
          throw new InvitationError("already_member");
        }
      }

      // Get person information for the email template
      const { data: person, error: personError } = await supabase
        .from("estates")
        .select("full_name, first_name, last_name")
        .eq("estate_id", estateId)
        .single();

      if (personError) {
        throw new Error(`Failed to get person information: ${personError.message}`);
      }

      // Generate a unique invitation token
      const invitationToken = crypto.randomUUID();

      // Set expiration to 7 days from now
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      // Create the invitation record
      const { data: invitation, error: invitationError } = await supabase
        .from("estate_invitations")
        .insert({
          estate_id: estateId,
          email: email.toLowerCase(),
          role: role,
          invitation_token: invitationToken,
          expires_at: expiresAt.toISOString(),
          status: "pending",
          invited_by_user_id: user.user_id,
          is_attorney: isAttorney ?? false,
        })
        .select()
        .single();

      if (invitationError) {
        throw new Error(`Failed to create invitation: ${invitationError.message}`);
      }

      // Update the invited field in estate_contacts table
      if (existingContact) {
        const { error: updateContactError } = await supabase
          .from("estate_contacts")
          .update({ invited: true })
          .eq("id", existingContact.id);

        if (updateContactError) {
          console.error("Failed to update contact invited status:", updateContactError);
          // Don't throw error here as the invitation was created successfully
        }
      }

      // Send invitation email
      // /accept-invitation?token=838a72d2-c3f6-409f-9bb9-a866890a8de4
      // const invitationLink = `${window.location.origin}/invitation/${invitationToken}`;
      const inviterName = encodeURIComponent(user.full_name ?? user.first_name ?? "Someone");
      const encodedDecedentName = encodeURIComponent(person.full_name);
      const invitationLink = isAttorney
        ? `${window.location.origin}${LAWYER_ROUTES.SIGNUP}?token=${invitationToken}&inviter=${inviterName}&email=${encodeURIComponent(email)}`
        : `${window.location.origin}/accept-invitation?token=${invitationToken}&inviter=${inviterName}&person=${encodedDecedentName}`;
      const inviterFirstName = user.first_name || user.full_name?.split(" ")[0] || "Someone";
      const inviteeFirstName = existingContact
        ? existingContact.first_name || email.split("@")[0]
        : email.split("@")[0];
      const decedentFullName = person.full_name || person.first_name || "Unknown";

      const { error: emailError } = await supabase.functions.invoke("communications/email/send", {
        body: {
          type: "invitation",
          to: email,
          user_id: user.user_id,
          data: {
            inviter_first_name: inviterFirstName,
            inviter_full_name: user.full_name || user.first_name || "Someone",
            invitee_first_name: inviteeFirstName,
            decedent_full_name: decedentFullName,
            cta_url: invitationLink,
          },
          metadata: {
            invitation_id: invitation.id,
            estate_id: estateId,
            role: role,
          },
        },
      });

      if (emailError) {
        console.error("Failed to send invitation email:", emailError);
        // Don't throw error here - invitation was created successfully
        // Just log the email error for debugging
      }

      // Send email to the inviter confirming the invitation was sent
      try {
        await supabase.functions.invoke("communications/email/send", {
          body: {
            type: "inviter",
            to: user.email!,
            user_id: user.user_id,
            data: {
              invitee_name: inviteeFirstName,
              inviter_name: user.full_name || user.first_name || "there",
              invitee_email: email,
              decedent_name: decedentFullName,
            },
            metadata: {
              invitation_id: invitation.id,
              estate_id: estateId,
              role: role,
            },
          },
        });
      } catch (inviterEmailError) {
        console.error("Failed to send inviter confirmation email:", inviterEmailError);
        // Don't throw error - invitation was created successfully
      }

      await logActivity({
        estate_id: estateId,
        action_type: "invited_contact",
        user_id: user.user_id,
        entity_type: existingContact ? "contact" : null,
        entity_id: existingContact ? String(existingContact.id) : null,
        metadata: {
          email: email.toLowerCase(),
          role,
          name: existingContact
            ? [existingContact.first_name, existingContact.last_name].filter(Boolean).join(" ") ||
              null
            : null,
        },
      });

      return invitation;
    },
    onSuccess: (_, variables) => {
      // Track Invite Sent event
      try {
        // Determine invite_type based on role: "admin" = advisor, "member" or "viewer" = family
        const inviteType = variables.role === "admin" ? "advisor" : "family";

        trackEvent(EventType.INVITE_SENT, {
          invite_type: inviteType,
          recipient_role: variables.role,
          estate_id: String(variables.estateId),
          invite_source: variables.analytics?.inviteSource ?? "other",
          invitee_role: variables.analytics?.inviteeRole,
          has_phone: variables.analytics?.hasPhone,
          has_personal_note: variables.analytics?.hasPersonalNote,
        });
      } catch (trackingError) {
        // Don't fail the operation if tracking fails
        console.error("Failed to track INVITE_SENT:", trackingError);
      }

      if (!variables.silent) {
        enqueueSnackbar(`Invitation sent to ${variables.email}`, {
          variant: "success",
        });
      }
      queryClient.invalidateQueries({
        queryKey: ["personInvitations", variables.estateId],
      });
      queryClient.invalidateQueries({
        queryKey: ["person-contacts", variables.estateId],
      });
    },
    onError: (error: Error) => {
      notifyInvitationFailure(error, "We couldn't send the invitation. Please try again.");
    },
  });

  // Resend invitation mutation
  const resendInvitation = useMutation({
    mutationFn: async ({ invitationId, email }: { invitationId: number; email: string }) => {
      if (!user?.user_id) {
        throw new Error("User not authenticated");
      }

      // Get the existing invitation details
      const { data: invitation, error: invitationError } = await supabase
        .from("estate_invitations")
        .select(
          `
          *,
          person:estates (
            full_name,
            first_name,
            last_name
          )
        `,
        )
        .eq("id", invitationId)
        .single();

      if (invitationError || !invitation) {
        throw new Error("Invitation not found");
      }

      // Generate a new invitation token and extend expiration
      const newInvitationToken = crypto.randomUUID();
      const expiresAt = new Date();
      expiresAt.setDate(expiresAt.getDate() + 7);

      // Update the invitation with new token and expiration
      const { error: updateError } = await supabase
        .from("estate_invitations")
        .update({
          invitation_token: newInvitationToken,
          expires_at: expiresAt.toISOString(),
        })
        .eq("id", invitationId);

      if (updateError) {
        throw new Error(`Failed to update invitation: ${updateError.message}`);
      }

      // Send the new invitation email
      const resendInviterFirstName = user.first_name || user.full_name?.split(" ")[0] || "Someone";
      const resendDecedentFullName =
        invitation.person?.full_name || invitation.person?.first_name || "Unknown";
      const resendInviterName = encodeURIComponent(user.full_name ?? user.first_name ?? "Someone");
      const resendEncodedDecedentName = encodeURIComponent(resendDecedentFullName);
      const invitationLink = invitation.is_attorney
        ? `${window.location.origin}${LAWYER_ROUTES.SIGNUP}?token=${newInvitationToken}&inviter=${resendInviterName}&email=${encodeURIComponent(email)}`
        : `${window.location.origin}/accept-invitation?token=${newInvitationToken}&inviter=${resendInviterName}&person=${resendEncodedDecedentName}`;

      const { error: emailError } = await supabase.functions.invoke("communications/email/send", {
        body: {
          type: "invitation",
          to: email,
          user_id: user.user_id,
          data: {
            inviter_first_name: resendInviterFirstName,
            inviter_full_name: user.full_name || user.first_name || "Someone",
            invitee_first_name: email.split("@")[0],
            decedent_full_name: resendDecedentFullName,
            cta_url: invitationLink,
          },
          metadata: {
            invitation_id: invitationId,
            estate_id: invitation.estate_id,
            resend: true,
          },
        },
      });

      if (emailError) {
        console.error("Failed to send invitation email:", emailError);
        // Don't throw error here - invitation was updated successfully
      }

      // Send email to the inviter confirming the invitation was resent
      try {
        await supabase.functions.invoke("communications/email/send", {
          body: {
            type: "inviter",
            to: user.email!,
            user_id: user.user_id,
            data: {
              invitee_name: email.split("@")[0],
              inviter_name: user.full_name || user.first_name || "there",
              invitee_email: email,
              decedent_name: resendDecedentFullName,
            },
            metadata: {
              invitation_id: invitationId,
              estate_id: invitation.estate_id,
              resend: true,
            },
          },
        });
      } catch (inviterEmailError) {
        console.error("Failed to send inviter confirmation email:", inviterEmailError);
        // Don't throw error - invitation was updated successfully
      }

      return {
        ...invitation,
        invitation_token: newInvitationToken,
        expires_at: expiresAt.toISOString(),
      };
    },
    onSuccess: (_, variables) => {
      enqueueSnackbar(`Invitation resent to ${variables.email}`, {
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["personInvitations"] });
      queryClient.invalidateQueries({
        queryKey: ["personInvitationsAccepted"],
      });
    },
    onError: (error: Error) => {
      notifyInvitationFailure(error, "We couldn't resend the invitation. Please try again.");
    },
  });

  // Accept invitation mutation - simplified for direct sharing
  const acceptInvitation = useMutation({
    mutationFn: async () => {
      throw new Error("Direct sharing is used instead of token-based invitations");
    },
    onSuccess: () => {
      enqueueSnackbar(TOAST.ACCOUNT_JOINED, {
        variant: "success",
      });
      queryClient.invalidateQueries({ queryKey: ["userContacts"] });
    },
    onError: () => {
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
    },
  });

  const cancelInvitation = useMutation({
    mutationFn: async (invitationId: number) => {
      if (!estateId) throw new Error("Estate ID is required to cancel an invitation");

      const { data: invitation, error: readError } = await supabase
        .from("estate_invitations")
        .select("email")
        .eq("id", invitationId)
        .eq("estate_id", estateId)
        .single();

      if (readError) throw readError;

      const { error } = await supabase
        .from("estate_invitations")
        .delete()
        .eq("id", invitationId)
        .eq("estate_id", estateId);

      if (error) throw error;

      if (invitation?.email) {
        await supabase
          .from("estate_contacts")
          .update({ invited: false })
          .eq("estate_id", estateId)
          .eq("email", invitation.email);
      }
    },
    onSuccess: () => {
      enqueueSnackbar("Invitation cancelled", { variant: "success" });
      queryClient.invalidateQueries({ queryKey: ["personInvitations", estateId] });
      queryClient.invalidateQueries({ queryKey: ["person-contacts", estateId] });
    },
    onError: () => {
      enqueueSnackbar(TOAST.GENERIC_ERROR, { variant: "error" });
    },
  });

  return {
    invitations: invitations || [],
    acceptedInvitations: acceptedInvitations || [],
    isLoadingInvitations,
    invitationsError,
    refetchInvitations,
    sendInvitation,
    resendInvitation,
    acceptInvitation,
    cancelInvitation,
    isSendingInvitation: sendInvitation.isPending,
    resendingInvitationEmail: resendInvitation.isPending
      ? (resendInvitation.variables?.email ?? null)
      : null,
    isAcceptingInvitation: acceptInvitation.isPending,
    isCancellingInvitation: cancelInvitation.isPending,
  };
}
